import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, money, fmtDate, statusColors, priorityColors, mesi, laundryLabels, laundryKeys, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Home } from "lucide-react";
import Documenti from "@/pages/Documenti";
import { ApartmentQuickActions, OverviewPanel, TodoPanel, PhotosPanel, ReviewsPanel } from "@/components/Apartment360";

const TABS = [
  { k: "panoramica", l: "Panoramica" },
  { k: "todo", l: "Todo" },
  { k: "dati", l: "Dati" },
  { k: "finanze", l: "Movimenti cassa" },
  { k: "pulizie", l: "Pulizie" },
  { k: "lavanderia", l: "Lavanderia" },
  { k: "consumabili", l: "Consumabili" },
  { k: "manutenzioni", l: "Manutenzioni" },
  { k: "rendiconti", l: "Rendiconti" },
  { k: "documenti", l: "Documenti" },
  { k: "foto", l: "Foto" },
  { k: "recensioni", l: "Recensioni" },
];

export default function ApartmentDetail() {
  const { id } = useParams();
  const [apt, setApt] = useState(null);
  const [owner, setOwner] = useState(null);
  const [prop, setProp] = useState(null);
  const [hist, setHist] = useState(null);
  const [ov, setOv] = useState(null);
  const [tab, setTab] = useState("panoramica");

  const loadOverview = () => http.get(`/apartments/${id}/overview`).then((r) => setOv(r.data)).catch(() => setOv(null));

  useEffect(() => {
    http.get(`/apartments/${id}`).then(async (r) => {
      setApt(r.data);
      if (r.data.owner_id) { try { setOwner((await http.get(`/owners/${r.data.owner_id}`)).data); } catch {} }
      if (r.data.property_id) { try { setProp((await http.get(`/properties/${r.data.property_id}`)).data); } catch {} }
    });
    http.get(`/apartments/${id}/history`).then((r) => setHist(r.data));
    loadOverview();
    // eslint-disable-next-line
  }, [id]);

  if (!apt || !hist) return <div className="text-slate-500">Caricamento...</div>;

  const totIn = hist.transactions.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
  const totOut = hist.transactions.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
  const cfg = apt.laundry_config || {};
  const persons = cfg.standard_persons || apt.capacity;
  const perCleaning = {
    double_sheets: (apt.double_beds || 0) * (cfg.double_sheets_per_double_bed ?? 2),
    single_sheets: (apt.single_beds || 0) * (cfg.single_sheets_per_single_bed ?? 2),
    pillowcases: (apt.double_beds || 0) * (cfg.pillowcases_per_double_bed ?? 2) + (apt.single_beds || 0) * (cfg.pillowcases_per_single_bed ?? 1),
    large_towels: persons * (cfg.large_towels_per_person ?? 1),
    medium_towels: persons * (cfg.medium_towels_per_person ?? 1),
    bath_mats: (apt.bathrooms || 0) * (cfg.bath_mats_per_bathroom ?? 1),
  };

  const counts = {
    todo: (ov?.tasks?.open || []).length,
    finanze: hist.transactions.length, pulizie: hist.cleanings.length, lavanderia: hist.laundry_costs.length,
    consumabili: hist.consumables.length, manutenzioni: hist.maintenance.length,
    rendiconti: hist.reports.length, documenti: hist.documents.length,
    foto: (ov?.photos || []).length, recensioni: ov?.reviews?.count || 0,
  };

  return (
    <div className="space-y-6" data-testid="apt-detail">
      <Link to="/appartamenti" className="text-sm text-slate-500 hover:text-teal-700 inline-flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Torna agli appartamenti</Link>
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-2xl bg-teal-100 text-teal-800 flex items-center justify-center shrink-0"><Home className="h-7 w-7" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-[var(--teal-deep)]">{apt.name}</h1>
          <div className="text-sm text-slate-500">{prop?.name || ""}{apt.city ? ` · ${apt.city}` : ""} · Gestione {apt.management_percent}% · Pulizia {money(apt.cleaning_cost)}</div>
        </div>
        {!apt.active && <Badge variant="outline" className="border-rose-300 text-rose-700 ml-2">NON ATTIVO</Badge>}
      </div>

      <ApartmentQuickActions apartmentId={id} onDone={() => { loadOverview(); http.get(`/apartments/${id}/history`).then((r) => setHist(r.data)); }} onGoDocs={() => setTab("documenti")} />

      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`apt-tab-${t.k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border min-h-[44px] ${tab===t.k?"bg-[var(--teal-deep)] text-white border-[var(--teal-deep)]":"bg-white text-slate-600 border-slate-200"}`}>
            {t.l}{counts[t.k] !== undefined ? ` (${counts[t.k]})` : ""}
          </button>
        ))}
      </div>

      {tab === "panoramica" && (ov ? <OverviewPanel ov={ov} /> : <div className="text-slate-400">Caricamento panoramica...</div>)}
      {tab === "todo" && (ov ? <TodoPanel ov={ov} reload={loadOverview} /> : null)}
      {tab === "foto" && (ov ? <PhotosPanel apartmentId={id} ov={ov} reload={loadOverview} /> : null)}
      {tab === "recensioni" && (ov ? <ReviewsPanel ov={ov} /> : null)}

      {tab === "dati" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-5 rounded-2xl space-y-2 text-sm">
            <div className="font-display font-bold text-[var(--teal-deep)]">Anagrafica</div>
            <div className="grid grid-cols-2 gap-2">
              <div><div className="text-slate-500 text-xs">Indirizzo</div><div className="font-medium">{apt.address || "-"}</div></div>
              <div><div className="text-slate-500 text-xs">Città</div><div className="font-medium">{apt.city || "-"}</div></div>
              <div><div className="text-slate-500 text-xs">Capienza</div><div className="font-medium">{apt.capacity}</div></div>
              <div><div className="text-slate-500 text-xs">Bagni</div><div className="font-medium">{apt.bathrooms}</div></div>
              <div><div className="text-slate-500 text-xs">Letti matrimoniali</div><div className="font-medium">{apt.double_beds}</div></div>
              <div><div className="text-slate-500 text-xs">Letti singoli</div><div className="font-medium">{apt.single_beds}</div></div>
              <div><div className="text-slate-500 text-xs">Base gestione</div><div className="font-medium">{apt.management_base}</div></div>
              <div><div className="text-slate-500 text-xs">Stato</div><div className="font-medium">{apt.active ? "Attivo" : "Non attivo"}</div></div>
            </div>
            {apt.notes && <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">{apt.notes}</div>}
          </Card>
          <Card className="p-5 rounded-2xl space-y-2 text-sm">
            <div className="font-display font-bold text-[var(--teal-deep)]">Proprietario</div>
            {owner ? <>
              <div className="font-medium">{owner.name}</div>
              <div className="text-xs text-slate-500">{owner.email || "-"} · {owner.phone || "-"}</div>
            </> : <div className="text-slate-400">Non assegnato</div>}
            <div className="font-display font-bold text-[var(--teal-deep)] pt-3">Set biancheria per pulizia (teorico)</div>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {laundryKeys.map((k) => <div key={k} className="flex justify-between"><span className="text-slate-500">{laundryLabels[k]}</span><span className="font-semibold">{perCleaning[k]}</span></div>)}
            </div>
          </Card>
          <Card className="p-4 rounded-2xl"><div className="text-xs uppercase text-slate-500">Entrate totali</div><div className="font-display text-2xl font-extrabold text-emerald-600 tabular-nums">{money(totIn)}</div></Card>
          <Card className="p-4 rounded-2xl"><div className="text-xs uppercase text-slate-500">Uscite totali</div><div className="font-display text-2xl font-extrabold text-rose-600 tabular-nums">{money(totOut)}</div></Card>
        </div>
      )}

      {tab === "finanze" && (
        <Card className="rounded-2xl p-4">
          <div className="space-y-2">
            {hist.transactions.length === 0 && <div className="text-center text-slate-400 py-8">Nessun movimento</div>}
            {hist.transactions.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                <div className="text-xs text-slate-500 w-24 tabular-nums">{fmtDate(t.date)}</div>
                <Badge variant="outline" className={t.type==="income"?"border-emerald-300 text-emerald-700":"border-rose-300 text-rose-700"}>{t.type==="income"?"Entrata":"Uscita"}</Badge>
                <div className="flex-1 text-sm text-slate-700 truncate">{t.counterparty || t.notes || "—"}</div>
                <div className={`tabular-nums font-semibold ${t.type==="income"?"text-emerald-600":"text-rose-600"}`}>{money(t.amount)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "pulizie" && (
        <Card className="rounded-2xl p-4">
          <div className="space-y-2">
            {hist.cleanings.length === 0 && <div className="text-center text-slate-400 py-8">Nessuna pulizia</div>}
            {hist.cleanings.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                <div className="text-xs text-slate-500 w-24 tabular-nums">{fmtDate(c.date)}</div>
                <div className="text-sm flex-1">{c.operator1_name || "-"}{c.operator2_name ? ` + ${c.operator2_name}` : ""}</div>
                <div className="tabular-nums font-semibold">{money(c.total_cost)}</div>
                {c.paid ? <Badge className="bg-emerald-100 text-emerald-800">Pagato</Badge> : <Badge variant="outline" className="border-amber-300 text-amber-800">No</Badge>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "lavanderia" && (
        <Card className="rounded-2xl p-4">
          <div className="space-y-2">
            {hist.laundry_costs.length === 0 && <div className="text-center text-slate-400 py-8">Nessun costo lavanderia</div>}
            {hist.laundry_costs.map((l) => (
              <div key={l.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                <div className="text-xs text-slate-500 w-28">{mesi[l.month]} {l.year}</div>
                <div className="text-sm flex-1">{l.supplier || "-"}</div>
                {l.document_id && <a href={fileUrl(l.document_id)} target="_blank" rel="noreferrer" className="text-xs text-teal-700 underline">PDF</a>}
                <div className="tabular-nums font-semibold">{money(l.amount)}</div>
                {l.paid ? <Badge className="bg-emerald-100 text-emerald-800">Pagato</Badge> : <Badge variant="outline" className="border-amber-300 text-amber-800">No</Badge>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "consumabili" && (
        <Card className="rounded-2xl p-4">
          <div className="space-y-2">
            {hist.consumables.length === 0 && <div className="text-center text-slate-400 py-8">Nessun consumabile</div>}
            {hist.consumables.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                <div className="text-xs text-slate-500 w-24 tabular-nums">{fmtDate(c.date)}</div>
                <Badge variant="outline" className="border-amber-300 text-amber-800">{c.category}</Badge>
                <div className="text-sm flex-1 truncate">{c.description || "-"}</div>
                <div className="tabular-nums font-semibold">{money(c.amount)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "manutenzioni" && (
        <Card className="rounded-2xl p-4">
          <div className="space-y-2">
            {hist.maintenance.length === 0 && <div className="text-center text-slate-400 py-8">Nessuna manutenzione</div>}
            {hist.maintenance.map((m) => (
              <div key={m.id} className="flex gap-2 items-center py-2 border-b border-slate-100 last:border-0">
                <div className="text-xs text-slate-500 w-24">{fmtDate(m.reported_date)}</div>
                <div className="flex-1 font-medium text-sm">{m.description}</div>
                <Badge variant="outline" className={priorityColors[m.priority]}>{m.priority}</Badge>
                <Badge variant="outline" className={statusColors[m.status]}>{m.status.replace("_"," ")}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "rendiconti" && (
        <Card className="rounded-2xl p-4">
          <div className="space-y-2">
            {hist.reports.length === 0 && <div className="text-center text-slate-400 py-8">Nessun rendiconto</div>}
            {hist.reports.map((r) => (
              <Link key={r.id} to={`/rendiconti/${r.id}`} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-4 px-4">
                <div className="text-sm w-40 font-medium">{mesi[r.month]} {r.year}</div>
                <Badge variant="outline" className={statusColors[r.status]}>{r.status}</Badge>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {tab === "documenti" && <Documenti apartmentId={id} />}
    </div>
  );
}
